import { useCallback, useState } from 'react';

/**
 * useState that remembers its value in sessionStorage under `key`.
 *
 * List pages use it for filters, search text and the open page/tab, so leaving
 * a page through the sidebar and coming back does not reset what was picked.
 * sessionStorage (not localStorage) on purpose: the choices should survive
 * navigation within the tab and die when the tab closes.
 *
 * Every storage call is guarded. Private-browsing modes and a full quota both
 * make sessionStorage throw, and a filter bar is not worth breaking a page over
 * - the value then simply lives in component state for the visit.
 *
 * @param {string} key   Storage key, namespaced per page, e.g. 'employees.status'.
 * @param {*} initialValue  Used when nothing is stored yet.
 * @returns {[*, Function]} The same pair useState returns.
 */
const readStored = (key, initialValue) => {
  try {
    const stored = sessionStorage.getItem(key);
    if (stored === null) return initialValue;
    return JSON.parse(stored);
  } catch {
    return initialValue;
  }
};

const usePersistedState = (key, initialValue) => {
  const [value, setValue] = useState(() => readStored(key, initialValue));

  const setPersistedValue = useCallback(
    (next) => {
      setValue((current) => {
        const resolved = typeof next === 'function' ? next(current) : next;
        try {
          sessionStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Storage unavailable - keep the value in memory for this visit only.
        }
        return resolved;
      });
    },
    [key]
  );

  return [value, setPersistedValue];
};

export default usePersistedState;
